import { overrideOrRegion } from '@/services/UserRegionService'
import { createCache } from '@/utils/cache'
import { logger } from '../logging'
import { Maybe } from '../types'

const LOCATION_API_URL = 'https://wheres-waldo-angel.herokuapp.com/ip/mine'

export type Location = {
  countryCode: Maybe<string>
  city: Maybe<string>
  stateCode: Maybe<string>
  postalCode: Maybe<string>
  latitude: Maybe<number>
  longitude: Maybe<number>
}

export interface UserLocation {
  hasSearchedLocation: boolean
  countryCode: Maybe<string>
  city: Maybe<string>
  coordinates: { latitude: number; longitude: number } | null
}

type FetchOptions = {
  timeout?: number
}
// prevents fetch from hanging
async function fetchWithTimeout(resource: string, options: FetchOptions) {
  const { timeout = 8000 } = options

  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(), timeout)
  const response = await fetch(resource, {
    signal: controller.signal,
  })
  clearTimeout(id)
  return response
}

async function fetchUserLocation(): Promise<UserLocation> {
  try {
    const response = await fetchWithTimeout(LOCATION_API_URL, { timeout: 3000 })
    const data = await response.json()
    const hasCoordinates = data?.latitude != null && data?.longitude != null
    return {
      hasSearchedLocation: true,
      countryCode: overrideOrRegion(data?.countryCode),
      city: data?.city,
      coordinates: hasCoordinates ? { latitude: data.latitude as number, longitude: data.longitude as number } : null,
    }
  } catch (e) {
    return {
      hasSearchedLocation: true,
      countryCode: '',
      city: '',
      coordinates: null,
    }
  }
}

const readUserLocationFromCache = createCache<UserLocation, UserLocation>(
  undefined,
  (val?: UserLocation) => val,
  fetchUserLocation,
)

export async function getUserLocation(): Promise<UserLocation | undefined> {
  try {
    const result = await readUserLocationFromCache()
    if (result.ok) {
      return result.value
    } else {
      logger().error('Failed to get user location', { error: result.error })
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (error?.name === 'AbortError') return
    logger().error('Failed to get user location', { error })
  }
}
